import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { dealsAPI } from '../api';
import Header from '../components/Header';
import PaidFeatureGate from '../components/PaidFeatureGate';
import { TrendingUp, CheckCircle2, AlertTriangle, TrendingDown, X, Plus, BarChart3, Trophy, Medal, Award, GitCompare, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const CompareDeals = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { lang } = useTranslation();
  
  const [savedDeals, setSavedDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const loadSavedDeals = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const userId = user?.user_id || 'demo_user';
      const response = await dealsAPI.getSaved(userId);
      setSavedDeals(response.data.deals || []);
    } catch (error) {
      console.error('Failed to load saved deals:', error);
      toast.error(lang === 'en' ? 'Failed to load saved deals' : 'Error al cargar tratos');
    } finally {
      setLoading(false);
    }
  }, [user, lang]);

  useEffect(() => {
    loadSavedDeals();
  }, [loadSavedDeals]);

  const dealStrategy = (deal) => (deal?.analysis_data?.strategy || deal?.strategy || 'flip').toLowerCase();

  // Rental metrics aren't carried in the backend rankings — read them from the
  // loaded saved deal (frontend-only) so rental rows can show Cash Flow / Cap Rate.
  const rentalOf = (rank) => {
    const d = savedDeals.find(x => x.saved_deal_id === rank.saved_deal_id);
    return d?.analysis_data?.rental_analysis || {};
  };

  const addDealToCompare = (deal) => {
    if (selectedDeals.length >= 3) {
      toast.error(lang === 'en' ? 'Maximum 3 deals can be compared' : 'Máximo 3 tratos para comparar');
      return;
    }
    if (selectedDeals.find(d => d.saved_deal_id === deal.saved_deal_id)) {
      toast.error(lang === 'en' ? 'Deal already selected' : 'Trato ya seleccionado');
      return;
    }
    // Same-category guard — the first selection locks the type; no mixed comparisons.
    // Surfaced as a centered modal (not a corner toast) so it can't be missed.
    if (selectedDeals.length > 0 && dealStrategy(deal) !== dealStrategy(selectedDeals[0])) {
      setShowCategoryModal(true);
      return;
    }
    setSelectedDeals([...selectedDeals, deal]);
    setComparison(null);
  };

  const removeDealFromCompare = (dealId) => {
    setSelectedDeals(selectedDeals.filter(d => d.saved_deal_id !== dealId));
    setComparison(null);
  };

  const runComparison = async () => {
    if (selectedDeals.length < 2) {
      toast.error(lang === 'en' ? 'Select at least 2 deals to compare' : 'Selecciona al menos 2 tratos');
      return;
    }

    setComparing(true);
    try {
      const userId = user?.user_id || 'demo_user';
      const response = await dealsAPI.compare({
        user_id: userId,
        saved_deal_ids: selectedDeals.map(d => d.saved_deal_id),
        title: `Comparison ${new Date().toLocaleDateString()}`,
        lang
      });
      setComparison(response.data);
      toast.success(lang === 'en' ? 'Comparison generated!' : '¡Comparación generada!');
    } catch (error) {
      console.error('Comparison failed:', error);
      // Surface the backend message (e.g. the same-category guard) if present.
      const detail = error.response?.data?.detail;
      toast.error(detail || (lang === 'en' ? 'Failed to compare deals' : 'Error al comparar tratos'));
    } finally {
      setComparing(false);
    }
  };

  const getDealBadge = (rating) => {
    const configs = {
      strong: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2 },
      moderate: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertTriangle },
      not_recommended: { bg: 'bg-red-100', text: 'text-red-800', icon: TrendingDown }
    };
    const config = configs[rating] || configs.moderate;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="w-3 h-3" />
      </span>
    );
  };

  const getRankBadge = (rank) => {
    const icons = [Trophy, Medal, Award];
    const colors = ['bg-yellow-400 text-yellow-900', 'bg-slate-300 text-slate-700', 'bg-amber-600 text-white'];
    const Icon = icons[rank - 1] || Award;
    return (
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors[rank - 1] || colors[2]}`}>
        <Icon className="w-5 h-5" />
      </div>
    );
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'border-yellow-400 bg-yellow-50';
    if (rank === 2) return 'border-slate-300 bg-slate-50';
    return 'border-amber-600 bg-amber-50';
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <PaidFeatureGate
        feature="compare_deals"
        featureDisplayName={lang === 'en' ? 'Compare Deals' : 'Comparar Tratos'}
        user={user}
        lang={lang}
        authIcon={GitCompare}
        authTitle={lang === 'en' ? 'Sign In to Compare Deals' : 'Inicia Sesión para Comparar Tratos'}
        authDescription={lang === 'en' 
          ? 'Create a free account or sign in to save and compare multiple investment opportunities side by side.'
          : 'Crea una cuenta gratuita o inicia sesión para guardar y comparar múltiples oportunidades de inversión.'}
        redirectAfterAuth="/compare"
      >
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl">
        {/* Back Navigation - Single button using browser history */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => {
              // Use browser history to return to previous page
              // Fallback to Saved Deals if no history (e.g., direct URL access)
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/saved');
              }
            }}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors group min-h-[44px] py-2 -ml-1 px-1 touch-manipulation"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {lang === 'en' ? 'Back' : 'Volver'}
          </button>
        </div>
        
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-1 sm:mb-2">
            {lang === 'en' ? 'Compare Deals' : 'Comparar Tratos'}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground">
            {lang === 'en' ? 'Select up to 3 deals to compare side-by-side' : 'Selecciona hasta 3 tratos para comparar'}
          </p>
        </div>

        {/* Selected Deals for Comparison */}
        <div className="bg-white rounded-xl shadow-sm border border-secondary/30 p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-secondary" />
                {lang === 'en' ? 'Selected for Comparison' : 'Seleccionados para Comparar'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {selectedDeals.length}/3 {lang === 'en' ? 'deals selected' : 'tratos seleccionados'}
                {selectedDeals.length < 2 && (
                  <span className="text-amber-600 ml-2">
                    ({lang === 'en' ? `need ${2 - selectedDeals.length} more` : `necesitas ${2 - selectedDeals.length} más`})
                  </span>
                )}
              </p>
            </div>
          </div>

          {selectedDeals.length === 0 ? (
            <div className="text-center py-8 sm:py-10 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50">
              <Plus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">
                {lang === 'en' ? 'No deals selected yet' : 'Sin tratos seleccionados'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {lang === 'en' ? 'Click on deals below to add them to comparison' : 'Haz clic en los tratos de abajo para agregarlos'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
                {selectedDeals.map((deal, index) => (
                  <div
                    key={deal.saved_deal_id}
                    className="relative bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-4 border-2 border-secondary/20 shadow-sm"
                    data-testid={`selected-deal-${index}`}
                  >
                    <button
                      onClick={() => removeDealFromCompare(deal.saved_deal_id)}
                      className="absolute top-2 right-2 p-1.5 bg-white hover:bg-red-50 rounded-full transition-colors shadow-sm"
                      data-testid={`remove-deal-${index}`}
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </button>
                    <div className="pr-8">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 bg-secondary text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {index + 1}
                        </span>
                        <h3 className="font-semibold text-primary truncate text-sm">
                          {deal.address || deal.analysis_data?.address}
                        </h3>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {deal.city || deal.analysis_data?.city}, {deal.state || deal.analysis_data?.state}
                      </p>
                      <div className="flex items-center justify-between">
                        {/* Valuation isn't a "deal" being rated — no score / no Weak-Strong badge */}
                        {dealStrategy(deal) !== 'valuation' && (
                          <div className="text-xl font-bold text-primary">
                            {Math.round(deal.deal_score || deal.analysis_data?.deal_score || 0)}/100
                          </div>
                        )}
                        {dealStrategy(deal) !== 'valuation' && getDealBadge(deal.deal_rating || deal.analysis_data?.deal_rating)}
                      </div>
                    </div>
                  </div>
                ))}
                {selectedDeals.length < 3 && (
                  <div 
                    className="border-2 border-dashed border-secondary/30 rounded-xl p-4 flex flex-col items-center justify-center text-secondary cursor-pointer hover:border-secondary hover:bg-blue-50/50 transition-all min-h-[120px]"
                    onClick={() => document.getElementById('available-deals')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Plus className="w-8 h-8 mb-2" />
                    <span className="font-medium text-sm">
                      {lang === 'en' ? `Add Deal` : `Agregar Trato`}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({3 - selectedDeals.length} {lang === 'en' ? 'slots available' : 'espacios disponibles'})
                    </span>
                  </div>
                )}
              </div>
              
              {/* Compare Button - Prominent CTA */}
              {selectedDeals.length >= 2 && (
                <button
                  onClick={runComparison}
                  disabled={comparing}
                  className="w-full py-3 sm:py-4 bg-gradient-to-r from-secondary to-blue-600 text-white rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl"
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      {lang === 'en' ? 'Comparing...' : 'Comparando...'}
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-6 h-6" />
                      {lang === 'en' ? `Compare ${selectedDeals.length} Deals Now` : `Comparar ${selectedDeals.length} Tratos Ahora`}
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {/* Comparison Results */}
        {comparison && (
          <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-6 sm:mb-8" data-testid="comparison-results">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-secondary/10 rounded-full flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-secondary" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-primary">
                {lang === 'en' ? 'Comparison Results' : 'Resultados de Comparación'}
              </h2>
            </div>
            
            {/* Rankings */}
            <div className="mb-6 sm:mb-8">
              <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                {lang === 'en' ? 'Deal Rankings (Best to Worst)' : 'Ranking de Tratos (Mejor a Peor)'}
              </h3>
              <div className="space-y-3">
                {comparison.rankings?.map((rank, index) => (
                  <div
                    key={index}
                    className={`flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-4 sm:p-5 rounded-xl border-2 ${getRankColor(rank.rank)} transition-all`}
                    data-testid={`ranking-${rank.rank}`}
                  >
                    <div className="flex items-center gap-4">
                      {getRankBadge(rank.rank)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold uppercase text-muted-foreground">
                            #{rank.rank} {lang === 'en' ? 'Best' : 'Mejor'}
                          </span>
                        </div>
                        <div className="font-bold text-primary text-lg">{rank.address}</div>
                        <div className="text-sm text-muted-foreground">{rank.city}, {rank.state}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 sm:ml-auto">
                      {/* Valuation isn't scored — show no deal-score cell for valuation rows */}
                      {rank.strategy !== 'valuation' && (
                        <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                          <div className="text-xl sm:text-2xl font-bold text-primary">{Math.round(rank.deal_score || 0)}/100</div>
                          <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Score' : 'Puntuación'}</div>
                        </div>
                      )}
                      {/* Strategy-aware metric columns — no flip fields on valuation/rental rows */}
                      {rank.strategy === 'valuation' ? (
                        <>
                          <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                            <div className="text-xl sm:text-2xl font-bold text-primary">${(rank.arv || 0).toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Estimated Value' : 'Valor Estimado'}</div>
                          </div>
                          <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                            <div className={`text-xl sm:text-2xl font-bold ${(rank.equity || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${(rank.equity || 0).toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Potential Equity' : 'Capital Potencial'}</div>
                          </div>
                        </>
                      ) : rank.strategy === 'rental' ? (
                        <>
                          <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                            <div className={`text-xl sm:text-2xl font-bold ${(rentalOf(rank).monthly_cashflow || rentalOf(rank).monthly_cash_flow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>${(rentalOf(rank).monthly_cashflow || rentalOf(rank).monthly_cash_flow || 0).toLocaleString()}</div>
                            <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Monthly Cash Flow' : 'Flujo Mensual'}</div>
                          </div>
                          <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                            <div className="text-xl sm:text-2xl font-bold text-secondary">{(rentalOf(rank).cap_rate || 0).toFixed(1)}%</div>
                            <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Cap Rate' : 'Tasa Cap'}</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                            <div className={`text-xl sm:text-2xl font-bold ${rank.net_profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${rank.net_profit?.toLocaleString()}
                            </div>
                            <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Net Profit' : 'Ganancia'}</div>
                          </div>
                          <div className="text-center px-3 py-1 bg-white rounded-lg shadow-sm">
                            <div className="text-xl sm:text-2xl font-bold text-secondary">{rank.profit_margin?.toFixed(1)}%</div>
                            <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Margin' : 'Margen'}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Winner Reasoning */}
            {comparison.winner_reasoning && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border border-green-200">
                <h3 className="text-base sm:text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  {lang === 'en' ? 'Investment Recommendation' : 'Recomendación de Inversión'}
                </h3>
                <p className="text-green-700 text-sm sm:text-base leading-relaxed">{comparison.winner_reasoning}</p>
              </div>
            )}
            
            {/* Action buttons after comparison */}
            <div className="flex flex-wrap gap-3 mt-6 pt-6 border-t border-border">
              <button
                onClick={() => {
                  setSelectedDeals([]);
                  setComparison(null);
                }}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-all text-sm"
              >
                {lang === 'en' ? 'New Comparison' : 'Nueva Comparación'}
              </button>
              <button
                onClick={() => navigate('/analyze')}
                className="px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-all text-sm"
              >
                {lang === 'en' ? 'Analyze More Properties' : 'Analizar Más Propiedades'}
              </button>
            </div>
          </div>
        )}

        {/* Available Deals */}
        <div id="available-deals" className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-muted-foreground" />
            {lang === 'en' ? 'Your Saved Deals' : 'Tus Tratos Guardados'}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({savedDeals.filter(d => !selectedDeals.find(s => s.saved_deal_id === d.saved_deal_id)).length} {lang === 'en' ? 'available' : 'disponibles'})
            </span>
          </h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">{lang === 'en' ? 'Loading deals...' : 'Cargando tratos...'}</p>
            </div>
          ) : savedDeals.length === 0 ? (
            <div className="text-center py-8">
              <TrendingUp className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                {lang === 'en' ? 'No saved deals to compare' : 'Sin tratos guardados para comparar'}
              </p>
              <button
                onClick={() => navigate('/analyze')}
                className="px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-all text-sm"
              >
                {lang === 'en' ? 'Analyze Properties First' : 'Analiza Propiedades Primero'}
              </button>
            </div>
          ) : (
            <div className="grid gap-2 sm:gap-3">
              {savedDeals
                .filter(d => !selectedDeals.find(s => s.saved_deal_id === d.saved_deal_id))
                .map((deal, index) => (
                  <div
                    key={deal.saved_deal_id || index}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg border border-border hover:border-secondary hover:bg-blue-50 transition-all cursor-pointer"
                    onClick={() => addDealToCompare(deal)}
                    data-testid={`available-deal-${index}`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-sm sm:text-base truncate">{deal.address || deal.analysis_data?.address}</h3>
                          {dealStrategy(deal) !== 'valuation' && getDealBadge(deal.deal_rating || deal.analysis_data?.deal_rating)}
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {deal.city || deal.analysis_data?.city}, {deal.state || deal.analysis_data?.state}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
                      {/* Valuation isn't scored — no deal-score on valuation rows */}
                      {dealStrategy(deal) !== 'valuation' && (
                        <div className="text-left sm:text-right">
                          <div className="text-base sm:text-lg font-bold">{Math.round(deal.deal_score || deal.analysis_data?.deal_score || 0)}/100</div>
                          <div className="text-xs text-muted-foreground">{lang === 'en' ? 'Deal Score' : 'Puntuación'}</div>
                        </div>
                      )}
                      <Plus className="w-5 h-5 text-secondary flex-shrink-0" />
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      </PaidFeatureGate>

      {/* Same-category required — centered modal (matches the app's overlay pattern) */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowCategoryModal(false)}
          data-testid="same-category-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl border border-slate-200 max-w-md w-full p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              {lang === 'en' ? 'Same Category Required' : 'Misma Categoría Requerida'}
            </h3>
            <p className="text-slate-600 mb-6 leading-relaxed">
              {lang === 'en'
                ? 'Comparisons must be between deals of the same category. Please select deals that are all the same type (all flips, all rentals, or all valuations).'
                : 'Las comparaciones deben ser de la misma categoría. Por favor selecciona deals que sean todos del mismo tipo (todos flips, todas rentas, o todas valuaciones).'}
            </p>
            <button
              onClick={() => setShowCategoryModal(false)}
              className="px-6 py-2.5 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              data-testid="same-category-ok"
            >
              {lang === 'en' ? 'OK' : 'Aceptar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareDeals;
